import crypto from 'crypto';
import hexToUuid from 'hex-to-uuid';
import { mapToConvoAIProductName } from './prompt-requests/utils/get-convoai-supported-product-name';

// Note -- the expectation is this type and the related EditorPluginAIPromptResponseMarkdown
// will be collapsed when we migrate to a single endpoint for all use cases.

/**
 * Only send the X-Product header if the request is to the assistance-service
 * Exported for testing purposes
 */
export const getXProductHeader = product => {
  return {
    'X-Product': mapToConvoAIProductName(product)
  };
};

/**
 * Creates the chat channelID for use with ConvoAI/assistance-service API.
 * A stable UUID is based on URL + Agent ID + User ID + Cloud ID.
 *
 * Exported only for unit testing.
 */

export const createChannelId = context => {
  const {
    contentURL,
    agentId,
    cloudId,
    userId
  } = context;
  const input = `${contentURL}-${agentId}-${cloudId}-${userId}`;

  // Rifat's voodoo to replicate Java's hashing implementation
  // @see https://atlassian.slack.com/archives/C075C6PPJ01/p1716960655383639?thread_ts=1716957128.598589&cid=C075C6PPJ01
  const md5Bytes = crypto.createHash('md5').update(input).digest();
  md5Bytes[6] &= 0x0f; // clear version
  md5Bytes[6] |= 0x30; // set to version 3
  md5Bytes[8] &= 0x3f; // clear variant
  md5Bytes[8] |= 0x80; // set to IETF variant
  return hexToUuid(md5Bytes.toString('hex'));
};

/**
 * Intended for use with streaming endpoints
 * https://hello.atlassian.net/wiki/spaces/CA3/pages/2485466879/Generative+AI+API+Specs
 *
 */
export async function* startStreamingResponse({
  endpoint,
  payload,
  abortController,
  getFetchCustomHeaders,
  product,
  // These are for convoai/assistance-service
  isStatefulEndpoint = false,
  experienceId = 'editor',
  agentNameId = 'editor_agent',
  getChannelVariables
}) {
  try {
    // create channelId if needed
    let channelId;
    let actualEndpoint = endpoint;
    if (isStatefulEndpoint) {
      if (!getChannelVariables) {
        // This shouldn't happen as it is checked early on
        throw new Error('getChannelVariables is required for stateful endpoints');
      }
      const channelVariables = getChannelVariables('rovo-agent');
      if (!channelVariables) {
        throw new Error('getChannelVariables returned null for rovo-agent context');
      }
      channelId = createChannelId({
        ...channelVariables,
        agentId: agentNameId
      });
      actualEndpoint = `${endpoint}/${channelId}/message/stream`;

      // TODO: clean this up + move creation of channelID earlier into UX
      // For some reason this blocks the stream
      // yield {
      // 	state: 'created-channel',
      // 	data: { channelId },
      // };
    }
    const fetchInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'X-Experience-Id': experienceId,
        ...getXProductHeader(product)
      },
      // When we move to a single api endpoint we will be able to collapse
      // the current logic spread across;
      // - api-fetch
      // - generate, transform, summarise
      // - mapResponseForSingleKeyToMarkdown
      // - and various config items
      // In the meantime -- have opted into this stringify -> parse -> stringify
      // to keep the shapes of startStreamingResponse and startSingleResponse aligned.
      body: payload,
      credentials: 'include',
      signal: abortController.signal,
      mode: 'cors'
    };
    const fetchCustomHeaders = (getFetchCustomHeaders === null || getFetchCustomHeaders === void 0 ? void 0 : getFetchCustomHeaders(endpoint, fetchInit)) || {};
    const response = await fetch(actualEndpoint, {
      ...fetchInit,
      headers: {
        ...fetchInit.headers,
        ...fetchCustomHeaders
      }
    });
    if (!response.ok) {
      // For non 200 status codes

      // https://atlassian.slack.com/archives/C055QF79V7W/p1684881764142449?thread_ts=1684802293.771169&cid=C055QF79V7W
      if (response.status === 400) {
        try {
          // TODO PREEAP
          // At the time of writing, the api returns the following:
          // { "error": { "key": "ERROR_KEY", ... } }
          // See
          // - https://hello.atlassian.net/wiki/spaces/CA3/pages/2485466879/Generative+AI+API+Specs#Error-Codes
          // - https://hello.atlassian.net/wiki/spaces/CA3/pages/2485466879/Generative+AI+API+Specs#Error-Codes.1
          // - https://hello.atlassian.net/wiki/spaces/CA3/pages/2485466879/Generative+AI+API+Specs#Error-codes
          // for details

          // Errors with special handling
          const knownErrors = [
          // We have soft alignment with the BE that for inputs that are not suitable
          // for prompts -- we want to rely on openai providing a suitable message for
          // the user. And not have manually maintained guards/user feedback messages.
          // https://product-fabric.atlassian.net/wiki/spaces/EUXQ/pages/3573548013/Introducing+AI+Dev+Documentation+TODO+AIFOLLOWUP+audit
          'INPUT_TOO_SHORT_TO_SUMMARIZE', 'INPUT_TOO_SHORT_TO_PROCESS', 'INPUT_EXCEEDS_TOKEN_LIMIT'];
          const {
            error
          } = await response.json();
          if (error) {
            if (typeof error === 'object' && 'key' in error && knownErrors.includes(error.key)) {
              return yield {
                state: 'failed',
                reason: 'backend-input-guard',
                guard: error.key,
                statusCode: response.status
              };
            }
            return yield {
              state: 'failed',
              reason: 'backend',
              error: 'Unhandled error response received',
              statusCode: response.status
            };
          }
        } catch (backendError) {
          return yield {
            state: 'failed',
            reason: 'backend',
            error: backendError,
            statusCode: response.status
          };
        }
      }

      // TODO PREEAP -- ensure our handling matches eventual backend implementation
      // https://hello.atlassian.net/browse/CCSMART-371
      // TODO POSTEAP -- revisit guard handling to have remove backend guards
      // and instead have the backend return an openai response describing why
      // the request was unable to be handled. (Potentially with a flag indicating
      // the response was unable to be handled for some custom FE treatment/handling).
      if (response.status === 429) {
        // TODO POSTEAP
        // https://product-fabric.atlassian.net/browse/ED-18777
        // The BE are unable to add the header due to constraints with the service proxy
        // There is a ticket to add this via feature request
        // https://sdog.jira-dev.com/jira/servicedesk/projects/SPHELP/queues/custom/262/SPHELP-93
        // For now we are just hard coding the value
        let retryAfter = response.headers.get('Retry-After');
        if (!retryAfter) {
          retryAfter = '60';
        }
        return yield {
          state: 'failed',
          reason: 'rate-limited',
          retryAfter: Number(retryAfter),
          statusCode: response.status
        };
      }
      if (response.status === 451) {
        // This status code is being used to indicate that the request was unable to be handled
        // due to the content not meeting the Acceptable Use Policy.
        return yield {
          state: 'aup-violation',
          statusCode: 451
        };
      }
      return yield {
        state: 'failed',
        reason: 'backend',
        error: `unexpected response status: ${response.status}`,
        statusCode: response.status
      };
    }
    if (!response.body) {
      yield {
        state: 'failed',
        reason: 'network',
        error: 'response.body missing',
        statusCode: response.status
      };
      return;
    }
    try {
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let done = false;
      while (!done) {
        const {
          value,
          done: doneReading
        } = await reader.read();
        done = doneReading;
        const chunkValue = decoder.decode(value);
        buffer = buffer + chunkValue;
        // Split the buffer by line breaks
        const lines = buffer.split('\n');
        // Process all complete lines, except for the last one (which might be incomplete)
        while (lines.length > 1) {
          const line = lines.shift();
          const parsedData = JSON.parse(line);
          if (parsedData.generatedContent === null) {
            var _parsedData$error, _parsedData$error2;
            if (((_parsedData$error = parsedData.error) === null || _parsedData$error === void 0 ? void 0 : _parsedData$error.statusCode) === 429) {
              yield {
                state: 'failed',
                reason: 'rate-limited',
                retryAfter: 60,
                statusCode: 429
              };
              return;
            }
            if (((_parsedData$error2 = parsedData.error) === null || _parsedData$error2 === void 0 ? void 0 : _parsedData$error2.key) === 'RESPONSE_TOO_SIMILAR') {
              yield {
                state: 'failed',
                reason: 'response-too-similar',
                statusCode: 208,
                data: parsedData
              };
              return;
            }
          }
          yield {
            state: 'loading',
            data: parsedData
          };
        }
        // Keep the last (potentially incomplete) line in the buffer
        buffer = lines[0];
      }
      yield {
        state: 'loaded-stream'
      };
      return;
    } catch (parsingError) {
      yield {
        state: 'failed',
        reason: 'parsing',
        error: parsingError,
        statusCode: response.status
      };
      return;
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      yield {
        state: 'failed',
        reason: 'aborted',
        error,
        statusCode: error.status
      };
    } else if (error.cause === 'ConvoAIChannelCreationError') {
      yield {
        state: 'failed',
        reason: 'backend',
        error,
        statusCode: error.status
      };
    } else {
      yield {
        state: 'failed',
        reason: 'network',
        error,
        statusCode: error.status
      };
    }
    return;
  }
}